/**
 * The Turnstile token that rides on the resident-guest /phone/start.
 *
 * Ported from the consumer's `parkify-pay/consumer/src/lib/otpTurnstile.ts`
 * (#4049), which is where the reasoning and the incident history live. The
 * resident-guest screen had the bug that file fixed: it sent whatever token was
 * in state at tap time, '' included, with no wait, no age limit and no retry,
 * while TURNSTILE_PHONE_ENABLED bounces a tokenless or refused send with
 * `turnstile_required`. The resident then read the server's "Please complete
 * the challenge" with no challenge on screen.
 *
 * Same behaviour as the consumer gate: a send never presents a token twice or a
 * stale one, waits for a solve in flight instead of sending empty, never
 * remounts a widget that is still solving, and on `turnstile_required` gets a
 * new token and retries once. One addition, `failed()`: the tenant widget
 * reports a widget that will never produce a token (script blocked or
 * unreachable, render threw, unsupported browser), so a send stops waiting on it
 * at once and the next send mounts a fresh one.
 *
 * Framework-free so the node tests can drive it; ResidentGuest.tsx holds one via
 * useOtpTurnstile.
 */

/** Cloudflare tokens are valid for 300s. Leave room for clock skew and a tab
 *  that was suspended while its expiry timer should have fired. */
export const TOKEN_MAX_AGE_MS = 240_000
/** How long a send waits for a silent solve before trying without a token (the
 *  server may not be enforcing; if it is, the refusal is mapped to honest copy). */
export const SILENT_SOLVE_WAIT_MS = 20_000
/** How long a send waits once Cloudflare has shown a challenge to tap. */
export const INTERACTIVE_WAIT_MS = 120_000
/** A widget solving this long with nothing to show (no token, no challenge
 *  drawn) is treated as dead and remounted. Anything younger is left alone:
 *  remounting an in-flight solve restarts it from zero. */
export const STUCK_SOLVE_MS = 45_000

export type OtpTurnstileWait = 'checking' | 'challenge'

/** Shown only while Cloudflare is displaying an interactive challenge. */
export const CHALLENGE_COPY = 'Tap the security check below and we’ll send your code.'
/** A send the security check still refused after a fresh token and a retry. */
export const CHECK_FAILED_COPY = 'We couldn’t finish a quick security check. Tap Continue to try again.'

export class OtpTurnstileGate {
  readonly configured: boolean
  private readonly remount: () => void
  private readonly now: () => number
  private token = ''
  private issuedAt = 0
  /** When the current widget started solving: its render, or the last remount
   *  (a render is imminent). -Infinity until either happens, and again once the
   *  widget reports it cannot produce a token. */
  private solvingSince = -Infinity
  private interactive = false
  private waiter: ((token: string | undefined) => void) | null = null
  private listener: ((w: OtpTurnstileWait) => void) | null = null
  private extendWait: (() => void) | null = null

  constructor(opts: { configured: boolean; remount: () => void; now?: () => number }) {
    this.configured = opts.configured
    this.remount = opts.remount
    this.now = opts.now ?? (() => Date.now())
  }

  /** Wire to <Turnstile onRender>: a widget is being rendered and is solving. */
  rendered(): void {
    this.solvingSince = this.now()
  }

  /** Wire to <Turnstile onToken>: a token on solve, '' on expiry/error. */
  offer(token: string): void {
    if (!token) {
      // Turnstile retries errors and refreshes expired tokens itself
      // (retry / refresh-expired default to auto), so the widget is solving again.
      this.token = ''
      this.solvingSince = this.now()
      return
    }
    this.interactive = false
    const waiter = this.waiter
    if (waiter) {
      // Someone is waiting to send: hand it straight over and start the next one.
      this.waiter = null
      this.discard()
      waiter(token)
      return
    }
    this.token = token
    this.issuedAt = this.now()
  }

  /** Wire to <Turnstile onInteractive>: true while a challenge is on screen. */
  setInteractive(on: boolean): void {
    this.interactive = on
    if (on) this.extendWait?.()
    this.listener?.(on ? 'challenge' : 'checking')
  }

  /** Wire to <Turnstile onError>: this widget will never produce a token.
   *  A send waiting on it stops waiting now (it would only time out), and the
   *  next take() mounts a fresh widget. Nothing remounts here: a retry happens
   *  on a send, never in a loop. */
  failed(): void {
    this.token = ''
    this.interactive = false
    this.solvingSince = -Infinity
    const waiter = this.waiter
    if (waiter) {
      this.waiter = null
      waiter(undefined)
    }
  }

  /** Drop the current token and mount a fresh widget for the next one. */
  discard(): void {
    this.token = ''
    this.interactive = false
    this.solvingSince = this.now()
    this.remount()
  }

  private fresh(): boolean {
    return !!this.token && this.now() - this.issuedAt < TOKEN_MAX_AGE_MS
  }

  /**
   * A token for exactly one send, or undefined if none arrived in time. Taking
   * a token always discards it (single-use, whatever the send's outcome).
   */
  take(onWait?: (w: OtpTurnstileWait) => void): Promise<string | undefined> {
    if (!this.configured) return Promise.resolve(undefined)
    if (this.fresh()) {
      const t = this.token
      this.discard()
      return Promise.resolve(t)
    }
    // Stale token, or a widget that never rendered, failed, or has solved nothing
    // for STUCK_SOLVE_MS: start a new solve. A solve still in flight is waited
    // on, never restarted.
    const stuck = !this.interactive && this.now() - this.solvingSince > STUCK_SOLVE_MS
    if (this.token || stuck) this.discard()
    return new Promise((resolve) => {
      let timer: ReturnType<typeof setTimeout>
      const finish = (t: string | undefined) => {
        clearTimeout(timer)
        this.waiter = null
        this.listener = null
        this.extendWait = null
        resolve(t)
      }
      const arm = (ms: number) => {
        clearTimeout(timer)
        timer = setTimeout(() => {
          // Leave the widget solving; the next take() will pick its token up.
          finish(undefined)
        }, ms)
      }
      this.waiter = finish
      this.listener = onWait ?? null
      this.extendWait = () => arm(INTERACTIVE_WAIT_MS)
      arm(this.interactive ? INTERACTIVE_WAIT_MS : SILENT_SOLVE_WAIT_MS)
      onWait?.(this.interactive ? 'challenge' : 'checking')
    })
  }
}

/** The tenant API client throws an Error carrying the server's `code`
 *  (lib/api.ts `call`); the server's Turnstile refusal is `turnstile_required`
 *  for both a missing and a refused token (api/src/utils/turnstile.ts). */
export function isTurnstileRequired(err: unknown): boolean {
  return err instanceof Error && (err as { code?: unknown }).code === 'turnstile_required'
}

/**
 * Run one /phone/start with a fresh token. On `turnstile_required` it gets a
 * new token and retries ONCE, but only with a token; a second refusal, or no
 * token for the retry, throws CHECK_FAILED_COPY, never the server's "complete
 * the challenge" text, which describes a challenge this screen may not be showing.
 */
export async function sendWithTurnstile<T>(
  gate: OtpTurnstileGate,
  send: (token: string | undefined) => Promise<T>,
  onWait?: (w: OtpTurnstileWait) => void,
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    const token = await gate.take(onWait)
    // A turnstile_required refusal proved the server enforces: a second tokenless
    // send is a guaranteed 403 that only spends the per-IP limiter (5/min, shared
    // behind NAT).
    if (attempt > 0 && !token) {
      throw Object.assign(new Error(CHECK_FAILED_COPY), { status: 403, code: 'turnstile_required' })
    }
    try {
      return await send(token)
    } catch (err) {
      if (!isTurnstileRequired(err)) throw err
      // Without a sitekey the retry has no token, so the guard above ends it.
      if (attempt === 0) continue
      throw Object.assign(new Error(CHECK_FAILED_COPY), { status: 403, code: 'turnstile_required' })
    }
  }
}
